import { api, type MutOpts } from "@/api"
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"

import { type components, type paths } from "@/api/v1"
import { queryClient } from "@/lib/query-client"

// ==================== TYPES ====================
// All reused from the OpenAPI-generated schema — the backend documents every
// monitoring request/response and query param, so there are no local shapes or
// casts here. Regenerate with `bun api:sync` when the backend contract changes.
export type AlertDetail = components["schemas"]["AlertDetail"]
export type EscalationPolicy = components["schemas"]["EscalationPolicy"]
export type FacilityEscalationOverview =
  components["schemas"]["FacilityEscalationOverview"]
export type EscalationLayer = components["schemas"]["EscalationLayer"]
export type ProgrammeCatalogue = components["schemas"]["ProgrammeCatalogue"]
export type SurvivorshipPlan = components["schemas"]["SurvivorshipPlan"]
export type PaginatedTriageAlertList =
  components["schemas"]["PaginatedTriageAlertList"]
export type MonitoringStatus = components["schemas"]["MonitoringStatus"]
export type MonitoringConfig = components["schemas"]["MonitoringConfig"]
export type CoverageSummary = components["schemas"]["CoverageSummary"]
export type PatientTimeline = components["schemas"]["PatientTimeline"]
export type TimelineCheckIn = components["schemas"]["TimelineCheckIn"]
export type TimelineInlineAlert = components["schemas"]["TimelineInlineAlert"]
export type TimelineOpenAlert = components["schemas"]["TimelineOpenAlert"]
export type TimelineAnswer = components["schemas"]["TimelineAnswer"]
export type StaffLabEntry = components["schemas"]["StaffLabEntry"]
export type LabTest = components["schemas"]["TestEnum"]
export type LabValueDetail = components["schemas"]["LabValueDetail"]
export type LabValueList = components["schemas"]["LabValueList"]
export type LabValueConfirmation =
  components["schemas"]["LabValueConfirmation"]
export type Regimen = components["schemas"]["Regimen"]
export type RegimenAgent = components["schemas"]["RegimenAgent"]
export type Cycle = components["schemas"]["Cycle"]
export type OncologyAgent = components["schemas"]["OncologyAgent"]
export type OncologyAgentList = components["schemas"]["OncologyAgentList"]
export type StartRegimen = components["schemas"]["StartRegimen"]
export type RecordCycle = components["schemas"]["PatchedRecordCycle"]
export type DrugProgramme = components["schemas"]["DrugProgramme"]
export type RegimenStatusSummary = components["schemas"]["RegimenStatusSummary"]
export type RegimenCalendar = components["schemas"]["RegimenCalendar"]
export type RegimenCalendarDay = components["schemas"]["RegimenCalendarDay"]
export type RegimenSchedule = components["schemas"]["RegimenSchedule"]
export type ScheduledCheckIn = components["schemas"]["ScheduledCheckIn"]

/** Lab-values list filters — optional verification-state filter. Derived from
 * the generated schema. */
export type LabValuesParams = NonNullable<
  paths["/patients/{id}/monitoring/lab-values/"]["get"]["parameters"]["query"]
>

/** Facility scope — optional when the staff member belongs to exactly one
 * facility; required otherwise (out-of-facility → 404). Derived from the
 * generated schema, so it tracks the backend contract. */
export type FacilityScope = NonNullable<
  paths["/patients/monitoring/status/"]["get"]["parameters"]["query"]
>

/** Triage-queue filters: status/tier/programme/state + pagination. */
export type TriageQueueParams = NonNullable<
  paths["/patients/monitoring/triage-queue/"]["get"]["parameters"]["query"]
>

/** Escalation overview filters: search/status + pagination. */
export type EscalationOverviewParams = NonNullable<
  paths["/patients/monitoring/escalation-policies/"]["get"]["parameters"]["query"]
>

/** Survivorship cohort filters: search/status + pagination. */
export type SurvivorshipCohortParams = NonNullable<
  paths["/patients/monitoring/survivorship/"]["get"]["parameters"]["query"]
>
export type SurvivorshipCohortRow =
  components["schemas"]["SurvivorshipCohortRow"]
export type SurvivorshipFilterOptions =
  components["schemas"]["SurvivorshipFilterOptions"]
export type BulkEnrollRequest = components["schemas"]["BulkEnrol"]
export type BulkEnrollResult = components["schemas"]["BulkEnrolResult"]
export type BulkEnrollCadence = components["schemas"]["CadenceEnum"]

// ==================== QUERY KEYS ====================
export const monitoringKeys = {
  all: ["monitoring"] as const,

  config: (params?: FacilityScope) =>
    [...monitoringKeys.all, "config", { params }] as const,

  status: (params?: FacilityScope) =>
    [...monitoringKeys.all, "status", { params }] as const,

  triageQueue: (params?: TriageQueueParams) =>
    [...monitoringKeys.all, "triage-queue", { params }] as const,

  alert: (id: number) => [...monitoringKeys.all, "alert", id] as const,

  escalationPolicy: (params?: FacilityScope) =>
    [...monitoringKeys.all, "escalation-policy", { params }] as const,
  escalationOverview: (params?: EscalationOverviewParams) =>
    [...monitoringKeys.all, "escalation-overview", { params }] as const,
  coverageSummary: (params?: FacilityScope) =>
    [...monitoringKeys.all, "coverage-summary", { params }] as const,
  escalationLayers: (params?: FacilityScope) =>
    [...monitoringKeys.all, "escalation-layers", { params }] as const,

  programmes: () => [...monitoringKeys.all, "programmes"] as const,

  survivorshipPlans: (patientId: number) =>
    [...monitoringKeys.all, "survivorship-plans", patientId] as const,

  survivorshipCohort: (params?: SurvivorshipCohortParams) =>
    [...monitoringKeys.all, "survivorship-cohort", { params }] as const,
  survivorshipFilterOptions: (facilityId?: number) =>
    [...monitoringKeys.all, "survivorship-filter-options", { facilityId }] as const,

  timeline: (patientId: number, programme?: string) =>
    [...monitoringKeys.all, "timeline", patientId, { programme }] as const,

  labValues: (patientId: number, params?: LabValuesParams) =>
    [...monitoringKeys.all, "lab-values", patientId, { params }] as const,

  regimen: (patientId: number) =>
    [...monitoringKeys.all, "regimen", patientId] as const,
  regimenHistory: (patientId: number) =>
    [...monitoringKeys.all, "regimen-history", patientId] as const,
  oncologyAgents: (q?: string) =>
    [...monitoringKeys.all, "oncology-agents", { q }] as const,
  regimenSummary: (rid: number) =>
    [...monitoringKeys.all, "regimen-summary", rid] as const,
  regimenCalendar: (rid: number, days: number) =>
    [...monitoringKeys.all, "regimen-calendar", rid, { days }] as const,
  regimenSchedule: (rid: number) =>
    [...monitoringKeys.all, "regimen-schedule", rid] as const,
}

/** Everything a policy/layer write can affect: the policy, its layers, the
 * coverage summary, and the nav status (sign-off is cleared on any edit). */
const invalidateEscalationConfig = () => {
  queryClient.invalidateQueries({
    predicate: query =>
      query.queryKey[0] === "monitoring" &&
      [
        "escalation-policy",
        "escalation-layers",
        "coverage-summary",
        "status",
        // Sign-off is go-live and an edit pauses monitoring, so the config
        // (pause/resume state) changes alongside the policy.
        "config",
      ].includes(query.queryKey[1] as string),
  })
}

// ==================== NAV / STATUS ====================

/** Facility monitoring status for the CareOS nav — is the module on, is the
 * policy signed off, and the open-alert count for the badge. */
export const useMonitoringStatus = (
  params?: FacilityScope,
  options?: { enabled?: boolean; refetchInterval?: number }
) => {
  return useQuery({
    queryKey: monitoringKeys.status(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/status/", { params: { query: params } })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 30 * 1000, // 30s — polled on the nav
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  })
}

/** Facility monitoring on/off config — the live switch + its gates (signed off,
 * module flag). */
export const useMonitoringConfig = (params?: FacilityScope) => {
  return useQuery({
    queryKey: monitoringKeys.config(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/config/", { params: { query: params } })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 30 * 1000,
  })
}

/** Turn facility monitoring on/off. Enabling needs a signed-off policy (400
 * otherwise); disabling is always allowed. Refreshes the nav status too. */
export const useUpdateMonitoringConfig = (
  mutationOptions?: MutOpts<"put", "/patients/monitoring/config/">
) => {
  return useMutation({
    mutationFn: fetchOpts => api.PUT("/patients/monitoring/config/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === "monitoring" &&
          ["config", "status"].includes(query.queryKey[1] as string),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

// ==================== TRIAGE QUEUE ====================

/** The facility-wide alert queue. Server sort is tier → unacknowledged →
 * oldest — don't re-sort client-side. The 200 envelope carries `serverTime`
 * for the polling clock. Pass `refetchInterval` from the component (~30s). */
export const useTriageQueue = (
  params?: TriageQueueParams,
  options?: { enabled?: boolean; refetchInterval?: number }
) => {
  return useQuery({
    queryKey: monitoringKeys.triageQueue(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/triage-queue/", {
          params: { query: params },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 0, // always fresh — this is a live clinical worklist
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  })
}

// ==================== ALERT DETAIL + ACTIONS ====================

/** One alert plus the evidence (item + attribute + answer) that caused it.
 * Never a score. */
export const useAlertDetail = (
  alertId: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitoringKeys.alert(alertId),
    queryFn: () =>
      api
        .GET("/patients/monitoring/alerts/{id}/", {
          params: { path: { id: alertId } },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 0,
    enabled: (options?.enabled ?? true) && !!alertId,
  })
}

/** Acknowledge an alert — stops the escalation clock. Idempotent: a second
 * call returns 200 with the existing acknowledgedBy, so an optimistic update
 * is safe. */
export const useAcknowledgeAlert = (
  mutationOptions?: MutOpts<
    "post",
    "/patients/monitoring/alerts/{id}/acknowledge/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/monitoring/alerts/{id}/acknowledge/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      const id = args[1]?.params?.path?.id
      if (id != null) {
        queryClient.invalidateQueries({ queryKey: monitoringKeys.alert(id) })
      }
      queryClient.invalidateQueries({
        queryKey: [...monitoringKeys.all, "triage-queue"],
      })
      queryClient.invalidateQueries({
        queryKey: [...monitoringKeys.all, "timeline"],
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Resolve an alert with a disposition. Resolving an already-resolved alert is
 * 409 — surface `resolvedBy` and refresh rather than treating it as a hard
 * error. */
export const useResolveAlert = (
  mutationOptions?: MutOpts<"post", "/patients/monitoring/alerts/{id}/resolve/">
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/monitoring/alerts/{id}/resolve/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      const id = args[1]?.params?.path?.id
      if (id != null) {
        queryClient.invalidateQueries({ queryKey: monitoringKeys.alert(id) })
      }
      queryClient.invalidateQueries({
        queryKey: [...monitoringKeys.all, "triage-queue"],
      })
      queryClient.invalidateQueries({
        queryKey: [...monitoringKeys.all, "timeline"],
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

// ==================== ESCALATION POLICY + LAYERS ====================

/** A facility's escalation policy (out-of-hours message + after-hours
 * contact). GET with no saved policy returns an empty unsaved shape. */
export const useEscalationPolicy = (
  params?: FacilityScope,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitoringKeys.escalationPolicy(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/escalation-policy/", {
          params: { query: params },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 60 * 1000, // 1 min — config
    enabled: options?.enabled ?? true,
  })
}

/** Escalation status across every facility the staff member can access — one
 * paginated row per facility (including unconfigured ones), searchable and
 * status-filterable server-side. Powers the multi-facility overview. */
export const useEscalationOverview = (params?: EscalationOverviewParams) => {
  return useQuery({
    queryKey: monitoringKeys.escalationOverview(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/escalation-policies/", {
          params: { query: params },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 30 * 1000,
  })
}

/** Edit the policy. Any successful write revokes sign-off, so refresh the
 * coverage summary and nav status too. */
export const useUpdateEscalationPolicy = (
  mutationOptions?: MutOpts<"put", "/patients/monitoring/escalation-policy/">
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.PUT("/patients/monitoring/escalation-policy/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateEscalationConfig()
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** The informed-sign-off confirm screen: per-tier windows, gaps, terminal
 * steps, and the blockers that keep `readyToSignOff` false. */
export const useCoverageSummary = (
  params?: FacilityScope,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitoringKeys.coverageSummary(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/escalation-policy/coverage-summary/", {
          params: { query: params },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/** Sign off + activate the policy. 400 with human-readable blockers if it
 * isn't safe. Flips the live gate on success. */
export const useSignOffEscalationPolicy = (
  mutationOptions?: MutOpts<
    "post",
    "/patients/monitoring/escalation-policy/sign-off/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/monitoring/escalation-policy/sign-off/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateEscalationConfig()
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** List a facility's escalation layers (grouped by tier, ordered by level in
 * the UI). */
export const useEscalationLayers = (
  params?: FacilityScope,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitoringKeys.escalationLayers(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/escalation-policy/layers/", {
          params: { query: params },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/** Add an escalation layer. Duplicate (tier, level) → 400. Revokes sign-off. */
export const useCreateEscalationLayer = (
  mutationOptions?: MutOpts<
    "post",
    "/patients/monitoring/escalation-policy/layers/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/monitoring/escalation-policy/layers/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateEscalationConfig()
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Edit one layer (partial). Duplicate (tier, level) → 400. Revokes sign-off. */
export const useUpdateEscalationLayer = (
  mutationOptions?: MutOpts<
    "patch",
    "/patients/monitoring/escalation-policy/layers/{id}/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.PATCH(
        "/patients/monitoring/escalation-policy/layers/{id}/",
        fetchOpts
      ),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateEscalationConfig()
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Delete one layer → 204. Revokes sign-off. */
export const useDeleteEscalationLayer = (
  mutationOptions?: MutOpts<
    "delete",
    "/patients/monitoring/escalation-policy/layers/{id}/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.DELETE(
        "/patients/monitoring/escalation-policy/layers/{id}/",
        fetchOpts
      ),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateEscalationConfig()
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

// ==================== SURVIVORSHIP ====================

/** The monitored-program catalog for the enrollment picker. Reference
 * data — rarely changes. */
export const useProgrammes = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: monitoringKeys.programmes(),
    queryFn: () =>
      api.GET("/patients/monitoring/programmes/").then(({ data, error }) => {
        if (error) throw error
        return data
      }),
    staleTime: Infinity,
    enabled: options?.enabled ?? true,
  })
}

/** A patient's survivorship plans (array). Only one open, non-ENDED plan
 * exists at a time. */
/** Facility-wide survivorship cohort — one paginated row per patient (incl.
 * unenrolled), searchable + status-filterable server-side. Powers the sidebar
 * survivorship landing. */
export const useSurvivorshipCohort = (params?: SurvivorshipCohortParams) => {
  return useQuery({
    queryKey: monitoringKeys.survivorshipCohort(params),
    queryFn: () =>
      api
        .GET("/patients/monitoring/survivorship/", {
          params: { query: params },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 30 * 1000,
  })
}

/** Dropdown options for the bulk-enroll filters (facilities, diagnoses,
 * clinicians, treatment phases) in one call. Pass the same facilityId used on
 * the cohort list so diagnoses/clinicians stay scoped to it. */
export const useSurvivorshipFilterOptions = (facilityId?: number) => {
  return useQuery({
    queryKey: monitoringKeys.survivorshipFilterOptions(facilityId),
    queryFn: () =>
      api
        .GET("/patients/monitoring/survivorship/filter-options/", {
          params: { query: facilityId != null ? { facilityId } : undefined },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 5 * 60 * 1000,
  })
}

/** Bulk-enrol many patients into a set of programmes in one request. Always
 * resolves 200 with a per-patient summary — read `enrolled`/`skipped` rather
 * than branching on status. Invalidates the cohort so enrolled rows refresh. */
export const useBulkEnrollSurvivorship = (
  mutationOptions?: MutOpts<
    "post",
    "/patients/monitoring/survivorship/bulk-enroll/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/monitoring/survivorship/bulk-enroll/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === "monitoring" &&
          query.queryKey[1] === "survivorship-cohort",
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useSurvivorshipPlans = (
  patientId: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitoringKeys.survivorshipPlans(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/monitoring/survivorship-plan/", {
          params: { path: { id: patientId } },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 60 * 1000,
    enabled: (options?.enabled ?? true) && !!patientId,
  })
}

/** Enrol a patient on a survivorship plan. Omitting programmeKeys enrols all
 * defaults; an existing open plan → 400. */
export const useEnrollSurvivorshipPlan = (
  mutationOptions?: MutOpts<
    "post",
    "/patients/{id}/monitoring/survivorship-plan/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/{id}/monitoring/survivorship-plan/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      const patientId = args[1]?.params?.path?.id
      if (patientId != null) {
        queryClient.invalidateQueries({
          queryKey: monitoringKeys.survivorshipPlans(patientId),
        })
        queryClient.invalidateQueries({
          queryKey: monitoringKeys.timeline(patientId),
        })
      }
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Amend the open plan — cadences, pause, resume, end. Open plan only (404 if
 * none); ending is terminal. */
export const useAmendSurvivorshipPlan = (
  mutationOptions?: MutOpts<
    "patch",
    "/patients/{id}/monitoring/survivorship-plan/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.PATCH("/patients/{id}/monitoring/survivorship-plan/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      const patientId = args[1]?.params?.path?.id
      if (patientId != null) {
        queryClient.invalidateQueries({
          queryKey: monitoringKeys.survivorshipPlans(patientId),
        })
        queryClient.invalidateQueries({
          queryKey: monitoringKeys.timeline(patientId),
        })
      }
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

// ==================== TIMELINE ====================

/** One patient's monitoring history — the `checkIns` stream (newest first, each
 * carrying its answers and inline alerts) cursor-paged with "load older", plus
 * the pinned `openAlerts` worklist on the first page. `?programme=` filters both
 * server-side. Render the raw timestamps in the viewer's timezone; no scores. */
export const usePatientMonitoringTimeline = (
  patientId: number,
  params?: { programme?: string }
) => {
  return useInfiniteQuery({
    queryKey: monitoringKeys.timeline(patientId, params?.programme),
    queryFn: ({ pageParam }) =>
      api
        .GET("/patients/{id}/monitoring/timeline/", {
          params: {
            path: { id: patientId },
            query: { programme: params?.programme, cursor: pageParam },
          },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage?.nextCursor ?? undefined,
    staleTime: 0,
    enabled: !!patientId,
  })
}

// ==================== LAB VALUES ====================

/** One patient's lab values for the oncology Labs tab — newest draw first, each
 * with its scan if it was scanned. `?verification=` filters server-side.
 * Returns the typed `LabValueList` ({ values: LabValueDetail[] }). */
export const useLabValues = (
  patientId: number,
  params?: LabValuesParams,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitoringKeys.labValues(patientId, params),
    queryFn: () =>
      api
        .GET("/patients/{id}/monitoring/lab-values/", {
          params: { path: { id: patientId }, query: params },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 0, // clinical values — always fresh
    enabled: (options?.enabled ?? true) && !!patientId,
  })
}

/** Staff enters a lab value (ANC / HGB / PLT). Confirmed on arrival — no
 * transcription review — and append-only, so a correction is a new entry, never
 * an edit. `LabValueError` → 400 (surface inline); no Staff row → 403;
 * out-of-tenant → 404. Refreshes the patient's lab trend on success. */
export const useCreateLabValue = (
  patientId: number,
  mutationOptions?: MutOpts<"post", "/patients/{id}/monitoring/lab-values/">
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/{id}/monitoring/lab-values/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [...monitoringKeys.all, "lab-values", patientId],
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Staff accepts or rejects a transcribed (scanned, patient-reported) lab
 * value: `POST .../lab-values/{value_id}/verify/` with { decision, note }.
 * 409 if it was already decided; 403 for a non-staff caller; 404 out-of-tenant.
 * On success, refresh the patient's lab values, the triage queue and any open
 * alert (a value's `needsVerification` clears once decided). */
export const useVerifyLabValue = (patientId?: number) => {
  return useMutation({
    mutationFn: ({
      valueId,
      decision,
      note,
    }: {
      valueId: number
      decision: components["schemas"]["DecisionEnum"]
      note?: string
    }) => {
      // The URL template keeps the snake_case token `{value_id}`, but the
      // generated path type camelCases it to `valueId`. openapi-fetch only
      // substitutes a key that matches the literal token, so the path must be
      // keyed `value_id` — otherwise the real id never interpolates and the
      // request hits `/lab-values/{value_id}/verify/` verbatim → 404. Both keys
      // are sent: `value_id` does the substitution, `valueId` satisfies the type.
      const path = { value_id: valueId, valueId }
      return api.POST(
        "/patients/monitoring/lab-values/{value_id}/verify/",
        { params: { path }, body: { decision, note: note ?? "" } }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === "monitoring" &&
          ["alert", "triage-queue"].includes(query.queryKey[1] as string),
      })
      if (patientId != null)
        queryClient.invalidateQueries({
          queryKey: [...monitoringKeys.all, "lab-values", patientId],
        })
    },
  })
}

// ==================== TREATMENT / REGIMEN ====================

/** Everything a regimen/cycle/status write can shift: the backend re-anchors
 * cycle dates on save, so always refetch the whole regimen. */
const invalidateRegimen = (patientId: number) => {
  queryClient.invalidateQueries({ queryKey: monitoringKeys.regimen(patientId) })
  // Summary/calendar/schedule are keyed by regimen id, not patient id, and a
  // cycle write re-anchors all of them — invalidate every one.
  queryClient.invalidateQueries({
    predicate: query =>
      query.queryKey[0] === "monitoring" &&
      [
        "regimen-history",
        "regimen-summary",
        "regimen-calendar",
        "regimen-schedule",
      ].includes(query.queryKey[1] as string),
  })
}

/** The authed client throws the parsed error body on any non-2xx, so the GET
 * for a patient with no regimen rejects with a `not_found` body. That's the
 * normal empty state, not an error — detect it so we return null once instead
 * of retrying a "failed" query. */
function isNoRegimen(err: unknown): boolean {
  if (err && typeof err === "object" && "errors" in err) {
    const { errors } = err as { errors?: unknown }
    if (Array.isArray(errors))
      return errors.some(
        e =>
          e != null &&
          typeof e === "object" &&
          (e as { code?: unknown }).code === "not_found"
      )
  }
  return false
}

/** A patient's current oncology regimen, or `null` when none has been started
 * (the backend 404s in that case — treated as "no regimen", not an error). */
export const useRegimen = (patientId: number) => {
  return useQuery({
    queryKey: monitoringKeys.regimen(patientId),
    queryFn: async () => {
      try {
        const { data } = await api.GET("/patients/{id}/monitoring/regimen/", {
          params: { path: { id: patientId } },
        })
        return data ?? null
      } catch (err) {
        if (isNoRegimen(err)) return null
        throw err
      }
    },
    staleTime: 30 * 1000,
    enabled: !!patientId,
  })
}

/** All of a patient's regimens (active + ended), newest first — the Treatment
 * tab's single source: the active/paused one drives the live view, the ended
 * ones are the history. */
export const useRegimenHistory = (patientId: number) => {
  return useQuery({
    queryKey: monitoringKeys.regimenHistory(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/monitoring/regimens/", {
          params: { path: { id: patientId } },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data ?? []
        }),
    staleTime: 30 * 1000,
    enabled: !!patientId,
  })
}

/** Header tiles: today's place in treatment (cycle, day, nadir, next check-in).
 * Fields are null off-treatment. Keyed by regimen id. */
export const useRegimenSummary = (rid: number | undefined) => {
  return useQuery({
    queryKey: monitoringKeys.regimenSummary(rid ?? 0),
    queryFn: () =>
      api
        .GET("/patients/monitoring/regimens/{rid}/summary/", {
          params: { path: { rid: rid as number } },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 60 * 1000,
    enabled: rid != null,
  })
}

/** Per-day treatment calendar to the horizon — dose days, nadir spans, and
 * check-in programme keys per day. Keyed by regimen id. */
export const useRegimenCalendar = (rid: number | undefined, days = 56) => {
  return useQuery({
    queryKey: monitoringKeys.regimenCalendar(rid ?? 0, days),
    queryFn: () =>
      api
        .GET("/patients/monitoring/regimens/{rid}/calendar/", {
          params: { path: { rid: rid as number }, query: { days } },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 60 * 1000,
    enabled: rid != null,
  })
}

/** Upcoming computed check-ins (with phase/intensity/window) for the schedule
 * detail. Keyed by regimen id. */
export const useRegimenSchedule = (rid: number | undefined) => {
  return useQuery({
    queryKey: monitoringKeys.regimenSchedule(rid ?? 0),
    queryFn: () =>
      api
        .GET("/patients/monitoring/regimens/{rid}/schedule/", {
          params: { path: { rid: rid as number } },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 60 * 1000,
    enabled: rid != null,
  })
}

/** The oncology drug catalog for the regimen picker. `q` filters by generic
 * or brand name. `schedulesSignedOff` is false while schedules are a draft. */
export const useOncologyAgents = (
  q?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: monitoringKeys.oncologyAgents(q),
    queryFn: () =>
      api
        .GET("/patients/monitoring/oncology-agents/", {
          params: { query: q ? { q } : undefined },
        })
        .then(({ data, error }) => {
          if (error) throw error
          return data
        }),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/** Start a regimen for a patient. Returns the created regimen. */
export const useStartRegimen = (
  patientId: number,
  mutationOptions?: MutOpts<"post", "/patients/{id}/monitoring/regimen/">
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/{id}/monitoring/regimen/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateRegimen(patientId)
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Add a cycle to a regimen (path `rid`). The engine may re-anchor later
 * cycles, so the regimen is refetched. */
export const useAddCycle = (
  patientId: number,
  mutationOptions?: MutOpts<
    "post",
    "/patients/monitoring/regimens/{rid}/cycles/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.POST("/patients/monitoring/regimens/{rid}/cycles/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateRegimen(patientId)
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Record/edit a cycle (path `cid`). A delay moves this cycle and the later
 * ones, so the regimen is refetched for the regenerated calendar. */
export const useUpdateCycle = (
  patientId: number,
  mutationOptions?: MutOpts<"patch", "/patients/monitoring/cycles/{cid}/">
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.PATCH("/patients/monitoring/cycles/{cid}/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateRegimen(patientId)
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

/** Set a regimen's status — ACTIVE / PAUSED / ENDED (path `rid`). Ending is
 * terminal and takes a reason. Returns the updated regimen. */
export const useSetRegimenStatus = (
  patientId: number,
  mutationOptions?: MutOpts<
    "patch",
    "/patients/monitoring/regimens/{rid}/status/"
  >
) => {
  return useMutation({
    mutationFn: fetchOpts =>
      api.PATCH("/patients/monitoring/regimens/{rid}/status/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateRegimen(patientId)
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}
